import React from "react";
import { Mail, Linkedin, Github, Gitlab, FileUser } from "lucide-react";
import resumeData from "../data/resumeData";

const Header = () => {
	const { personalInfo } = resumeData;

	return (
		<header className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-brand-950 text-white">
			{/* Subtle texture + glow */}
			<div
				className="pointer-events-none absolute inset-0 opacity-[0.18]"
				style={{
					backgroundImage:
						"radial-gradient(circle at 1px 1px, rgba(255,255,255,0.35) 1px, transparent 0)",
					backgroundSize: "22px 22px",
				}}
			></div>
			<div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl"></div>
			<div className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-brand-400/10 blur-3xl"></div>

			<div className="container mx-auto px-6 py-16 md:py-20 relative">
				<div className="flex flex-col lg:flex-row items-center">
					{/* Left side - Your information */}
					<div className="text-center lg:text-left lg:w-1/2 mb-8 lg:mb-0 z-10 animate-fade-up">
						<span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-brand-200 backdrop-blur-xs mb-5">
							<span className="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
							Available for opportunities
						</span>
						<h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4 text-white">
							{personalInfo.name}
						</h1>
						<p className="text-xl md:text-2xl font-light mb-3 text-slate-200">
							{personalInfo.title}
						</p>
						<p className="text-slate-400 mb-7">
							{personalInfo.education.school} •{" "}
							{personalInfo.education.degree}
						</p>

						{/* Contact links */}
						<div className="flex flex-wrap justify-center lg:justify-start gap-2.5 mb-6">
							<a
								href={`mailto:${personalInfo.email}`}
								className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-100 border border-white/10 bg-white/5 backdrop-blur-xs hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 transition-all duration-300"
							>
								<Mail size={16} />
								<span>{personalInfo.email}</span>
							</a>
							<a
								href={`https://${personalInfo.linkedin}`}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-100 border border-white/10 bg-white/5 backdrop-blur-xs hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 transition-all duration-300"
							>
								<Linkedin size={16} />
								<span>LinkedIn</span>
							</a>
							<a
								href={`https://${personalInfo.github}`}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-100 border border-white/10 bg-white/5 backdrop-blur-xs hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 transition-all duration-300"
							>
								<Github size={16} />
								<span>Personal GitHub</span>
							</a>
							<a
								href={`https://${personalInfo.github2}`}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-100 border border-white/10 bg-white/5 backdrop-blur-xs hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 transition-all duration-300"
							>
								<Github size={16} />
								<span>School GitHub</span>
							</a>
							<a
								href={`https://${personalInfo.gitlab}`}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-100 border border-white/10 bg-white/5 backdrop-blur-xs hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 transition-all duration-300"
							>
								<Gitlab size={16} />
								<span>Self-Hosted GitLab</span>
							</a>
							<a
								href={`/resume`}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-100 border border-white/10 bg-white/5 backdrop-blur-xs hover:bg-white/10 hover:border-white/25 hover:-translate-y-0.5 transition-all duration-300"
							>
								<FileUser size={16} />
								<span>Resume PDF</span>
							</a>
						</div>

						{/* Badges for skills/interests */}
						<div className="flex flex-wrap justify-center lg:justify-start gap-2">
							<span className="px-3 py-1 rounded-full text-xs font-medium text-slate-200 border border-white/10 bg-white/5">
								Computer Engineering
							</span>
							<span className="px-3 py-1 rounded-full text-xs font-medium text-slate-200 border border-white/10 bg-white/5">
								Robotics
							</span>
							<span className="px-3 py-1 rounded-full text-xs font-medium text-slate-200 border border-white/10 bg-white/5">
								Self-Hosting
							</span>
							<a
								target="_blank"
								rel="noopener noreferrer"
								href="https://www.vtbolt.com"
								className="px-3 py-1 rounded-full text-xs font-medium text-amber-200 border border-amber-300/25 bg-amber-400/10 hover:bg-amber-400/20 transition-colors"
							>
								BOLT Design Team
							</a>
							<span className="px-3 py-1 rounded-full text-xs font-medium text-slate-200 border border-white/10 bg-white/5">
								3D Printing
							</span>
						</div>
					</div>

					{/* Right side - image with gradient blend into the hero */}
					<div className="absolute top-0 right-0 bottom-0 hidden lg:block w-1/2">
						<img
							src="/images/bolt-photo.jpeg"
							alt="Mason with the BOLT electric superbike"
							className="h-full w-full object-cover object-center"
						/>
						<div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/40 to-transparent"></div>
						<div className="absolute inset-0 bg-gradient-to-t from-brand-950/60 to-transparent"></div>
					</div>
				</div>
			</div>

			{/* Mobile image (shown only on small screens) */}
			<div className="lg:hidden w-full px-6 pb-2 -mt-2 relative z-10">
				<div className="relative overflow-hidden rounded-2xl ring-1 ring-white/10">
					<img
						src="/images/bolt-photo.jpeg"
						alt="Mason with the BOLT electric superbike"
						className="w-full h-auto object-cover"
					/>
				</div>
			</div>

			{/* Soft fade into the page */}
			<div className="h-6 w-full bg-gradient-to-b from-transparent to-slate-50 relative z-10"></div>
		</header>
	);
};

export default Header;
